import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Clock, BadgeCheck, ArrowRight, Layers, Filter } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { pick, catLabel, levelLabel } from "../lib/pick";

const ALL = "all";

const programToCard = (p) => ({
  id: p.id,
  slug: `program-${p.id}`,
  href: `/programs/${p.id}`,
  title: p.title,
  title_en: p.title_en,
  subtitle: p.description,
  subtitle_en: p.description_en,
  description: p.description,
  description_en: p.description_en,
  category: p.category,
  level: p.level,
  hours: p.hours || 60,
  price: p.price,
  price_original: 0,
  badge_image: p.thumbnail,
  lessons_count: p.lesson_count,
  owned: !!p.enrolled,
  is_free: !p.price,
  is_live: p.mode === "live",
  start_date: p.start_date,
});

export default function Academy({ embedded = false, initialCategory = ALL }) {
  const { t, lang } = useLang();
  const [courses, setCourses] = useState(null);
  const [category, setCategory] = useState(initialCategory);
  const [priceBand, setPriceBand] = useState(ALL);

  useEffect(() => {
    Promise.all([
      api.get("/academy/courses").then((r) => r.data).catch(() => []),
      api.get("/courses").then((r) => r.data.map(programToCard)).catch(() => []),
    ]).then(([academy, programs]) => setCourses([...academy, ...programs]));
  }, []);
  useEffect(() => setCategory(initialCategory), [initialCategory]);

  const categories = useMemo(
    () => [ALL, ...Array.from(new Set((courses || []).map((c) => c.category).filter(Boolean)))],
    [courses]
  );

  const visible = (courses || []).filter((c) => {
    if (category !== ALL && c.category !== category) return false;
    if (priceBand === "free" && !c.is_free) return false;
    if (priceBand === "under50" && !(c.price > 0 && c.price <= 50)) return false;
    if (priceBand === "mid" && !(c.price > 50 && c.price <= 300)) return false;
    if (priceBand === "pro" && !(c.price > 300)) return false;
    return true;
  });

  const chip = (active, label, onClick, testid) => (
    <button
      key={label}
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.12em] border transition-colors duration-200 ${
        active ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-white text-[#334155] border-[#E2E8F0] hover:border-[#1D4ED8]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <>
      <div className={embedded ? "" : "max-w-7xl mx-auto px-5 sm:px-6 pt-14 pb-20"} data-testid="academy-page">
        {!embedded && (
          <>
        <span className="badge-lime">{t("academy.badge")}</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mt-5 text-[#0B1B3A]" data-testid="academy-title">
          {t("academy.title")}
        </h1>
        <p className="text-base text-[#475569] mt-4 max-w-3xl">{t("academy.subtitle")}</p>
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-9" data-testid="academy-filters">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#64748B] mr-2">
            <Filter className="w-3.5 h-3.5" /> {t("academy.filters")}
          </span>
          {categories.map((c) =>
            chip(category === c, c === ALL ? t("academy.allCategories") : catLabel(c, lang), () => setCategory(c), `academy-filter-category-${c}`)
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {[
            [ALL, t("academy.allPrices")],
            ["free", t("academy.freeOnly")],
            ["under50", "≤ 50€"],
            ["mid", "51–300€"],
            ["pro", "> 300€"],
          ].map(([value, label]) => chip(priceBand === value, label, () => setPriceBand(value), `academy-filter-price-${value}`))}
        </div>

        {courses === null ? (
          <p className="text-[#64748B] mt-10">{t("common.loading")}</p>
        ) : visible.length === 0 ? (
          <div className="card-dark p-12 mt-10 text-center text-[#64748B]" data-testid="academy-empty">{t("academy.empty")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="academy-course-grid">
            {visible.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.05, 0.4) }}
                className="card-3d overflow-hidden flex flex-col group"
                data-testid={`academy-card-${c.slug}`}
              >
                {c.badge_image && (
                  <div className="h-40 overflow-hidden">
                    <img src={c.badge_image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-testid={`academy-cover-${c.slug}`} />
                  </div>
                )}
                <div className="p-7 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3">
                  <span className="w-12 h-12 rounded-2xl bg-[#1D4ED8] flex items-center justify-center shrink-0">
                    <GraduationCap className="w-6 h-6 text-white" strokeWidth={2.3} />
                  </span>
                  {c.owned ? (
                    <span className="px-3 py-1.5 rounded-full bg-[#0B1B3A] text-white text-[10px] font-bold uppercase tracking-[0.14em]" data-testid={`academy-owned-${c.slug}`}>
                      {t("academy.owned")}
                    </span>
                  ) : c.is_live ? (
                    <span className="px-3 py-1.5 rounded-full bg-[#1D4ED8] text-white text-[10px] font-bold uppercase tracking-[0.14em]" data-testid={`academy-live-${c.slug}`}>
                      {t("live.badge")}
                    </span>
                  ) : c.is_free ? (
                    <span className="px-3 py-1.5 rounded-full bg-[#EEF4FF] text-[#1D4ED8] text-[10px] font-bold uppercase tracking-[0.14em]">
                      {t("academy.free")}
                    </span>
                  ) : null}
                </div>

                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#1D4ED8] mt-5">{catLabel(c.category, lang)}</p>
                <h2 className="font-heading font-black text-lg md:text-lg text-[#0B1B3A] mt-2 leading-snug">{pick(c, "title", lang)}</h2>
                <p className="text-sm text-[#475569] mt-3 flex-1">{pick(c, "subtitle", lang) || pick(c, "description", lang)}</p>

                <div className="flex flex-wrap gap-4 mt-5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#64748B]">
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {c.is_live ? t("live.durationV") : `${c.hours}h`}</span>
                  <span className="inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> {c.includes?.length ? `${c.includes.length} ${t("path.badge")}` : `${c.lessons_count} ${t("learn.lessons")}`}</span>
                  <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5" /> {levelLabel(c.level, lang)}</span>
                </div>

                <div className="flex items-end justify-between gap-3 mt-6 pt-5 border-t border-[#E2E8F0]">
                  <div>
                    {c.is_free ? (
                      <p className="font-heading font-black text-2xl text-[#0B1B3A]">{t("academy.free")}</p>
                    ) : (
                      <>
                        {c.offer_price ? (
                          <>
                            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#1D4ED8]" data-testid={`academy-offer-${c.slug}`}>
                              {(lang === "en" ? c.offer_label_en : c.offer_label)} · −{c.offer_pct}%
                            </p>
                            <p className="text-xs text-[#94A3B8] line-through">{c.price}€</p>
                            <p className="font-heading font-black text-2xl text-[#0B1B3A]" data-testid={`academy-price-${c.slug}`}>{c.offer_price}€</p>
                          </>
                        ) : (
                          <>
                            {c.price_original > c.price && (
                              <p className="text-xs text-[#94A3B8] line-through">{c.price_original}€</p>
                            )}
                            <p className="font-heading font-black text-2xl text-[#0B1B3A]" data-testid={`academy-price-${c.slug}`}>{c.price}€</p>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <Link data-testid={`academy-open-${c.slug}`} to={c.href || `/academy/${c.slug}`} className="btn-blue !px-5 !py-2.5 text-sm">
                    {c.owned ? t("academy.continue") : t("academy.details")} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      {!embedded && <Footer />}
    </>
  );
}
